import { z } from 'zod';

export const AssetStatus = {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
} as const;
export type AssetStatus = (typeof AssetStatus)[keyof typeof AssetStatus];

// we use looseObject here, since all values present in masterdata are displayed in AssetDetails
const masterDataSchema = z.looseObject({
    fuel_type: z.string().optional(),
});

export const assetSchema = z.object({
    id: z.string(),
    account_id: z.string(),
    identification: z.string().nullable().optional(),
    identification_type: z.string().nullable().optional(),
    name: z.string(),
    brand: z.string().nullable().optional(),
    status: z.enum([AssetStatus.ACTIVE, AssetStatus.ARCHIVED]),
    type: z.string(),
    license_plate: z.string().nullable().optional(),
    license_plate_country_code: z.string().nullable().optional(),
    _embedded: z
        .object({
            master_data: masterDataSchema.optional(),
        })
        .optional(),
});
export type Asset = z.infer<typeof assetSchema>;

export const deviceSchema = z.object({
    id: z.string(),
    identification: z.string(),
    type: z.string(),
});
export type Device = z.infer<typeof deviceSchema>;

export const associationSchema = z.object({
    id: z.string(),
    device_id: z.string(),
    asset_id: z.string(),
});
export type Association = z.infer<typeof associationSchema>;
export type Associations = Association[];

export const accountInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    tenant: z.string(),
    account_type: z.string(),
});
export type AccountInfo = z.infer<typeof accountInfoSchema>;

const paginationLinksSchema = z
    .object({
        next: z.object({ href: z.string() }).optional(),
        self: z.object({ href: z.string() }).optional(),
    })
    .optional();

export const assetsResponseSchema = z.object({
    items: z.array(assetSchema),
    _links: paginationLinksSchema,
});

export const devicesResponseSchema = z.object({
    items: z.array(deviceSchema),
});

export const associationsResponseSchema = z.object({
    items: z.array(associationSchema),
    _links: paginationLinksSchema,
});

export const associationsWithEmbeddedDevicesResponseSchema = z.object({
    items: z.array(
        associationSchema.extend({
            _embedded: z
                .object({
                    device: deviceSchema.optional(),
                })
                .optional(),
        })
    ),
    _links: paginationLinksSchema,
});

export const accountsResponseSchema = z.object({
    items: z.array(accountInfoSchema),
});

export const rentalCompaniesResponseSchema = z.object({
    items: z.array(
        z.object({
            id: z.string(),
        })
    ),
});
